/**
 * M9: the hulk tuning (`src/data/hulks.json`), validated by Zod. CH-2a wrote the placement block; CH-4 added every
 * other table, and the file is read-only for later cards (plan §4.0): the survey's scanner tier, the named flaws
 * with their stat multipliers, value and fix costs, the flaw-count weights, the First Rush pattern edges, the kit,
 * hazards and squatters by region, the scrap rule (CH-11), the off-screen combat factor (CH-9b) and the card text.
 * CH-8b (D-572 item 3) added the claimed-ship insurance cap (`insurance.putInShare`).
 */
import { z } from 'zod'
import { goodIdSchema } from '../career/parts'
import type { ShipDefRaw } from '../data/schemas'
import hulksJson from '../data/hulks.json'

const countRangeSchema = z
  .tuple([z.number().int().nonnegative(), z.number().int().nonnegative()])
  .refine(([a, b]) => a <= b, 'min > max')
const metresRangeSchema = z.tuple([z.number().nonnegative(), z.number().positive()]).refine(([a, b]) => a <= b, 'min > max')
const share = z.number().min(0).max(1)

/** One value per galaxy region (`galaxy/types.ts RegionId`). */
function perRegion<T extends z.ZodTypeAny>(value: T) {
  return z.object({ gate: value, mid: value, deep: value, rim: value }).strict()
}

/** Systems that never hold hulks: the home system and the M7 story sites' systems (H-1). */
export const hulkExclusionSchema = z.enum(['home', 'columnNine', 'okaforLab'])
export type HulkExclusion = z.infer<typeof hulkExclusionSchema>

/**
 * The `ships.json` stats a flaw or an edge may scale (numeric, on every ship def; CH-9a multiplies the flown def's
 * value). `mass` is the only one where higher is worse.
 */
export const HULK_STATS = [
  'hull',
  'shield',
  'shieldRegen',
  'thrustForward',
  'thrustReverse',
  'thrustLateral',
  'thrustVertical',
  'maxSpeed',
  'energy',
  'energyRegen',
  'mass',
] as const satisfies readonly (keyof ShipDefRaw)[]
export type HulkStat = (typeof HULK_STATS)[number]
export const hulkStatSchema = z.enum(HULK_STATS)

/** Bits 0..11 of the flaws bitmask are flaws, bits 12..15 the First Rush pattern edge (`flaws.ts`). */
export const MAX_FLAWS = 12
export const MAX_EDGES = 4

const textId = z.string().regex(/^[a-z][a-z0-9_]*$/)

/** A fix cost: per good, a share of the hull's value (Σ buildCost × base price), paid in whole units of that good. */
const fixSchema = z
  .partialRecord(goodIdSchema, z.number().positive())
  .refine((r) => Object.keys(r).length > 0, 'a fix names at least one good')

export const hulkFlawSchema = z
  .object({
    id: textId,
    name: z.string().min(1),
    stat: hulkStatSchema,
    /** The stat multiplier (< 1, or > 1 for `mass`). */
    mult: z.number().positive(),
    /** The hull's value multiplier for this flaw (insurance and value, CH-8); a hull's is the product. */
    valueMult: z.number().gt(0).max(1),
    /** Draw weight in the roll. */
    weight: z.number().positive(),
    fix: fixSchema,
  })
  .strict()
  .refine((f) => (f.stat === 'mass' ? f.mult > 1 : f.mult < 1), 'a flaw makes its stat worse')
export type HulkFlaw = z.infer<typeof hulkFlawSchema>

export const hulkEdgeSchema = z
  .object({
    id: textId,
    name: z.string().min(1),
    /** Never `mass`: an edge raises a higher-is-better stat. */
    stat: hulkStatSchema.exclude(['mass']),
    mult: z.number().gt(1),
  })
  .strict()
export type HulkEdge = z.infer<typeof hulkEdgeSchema>

const line = z.string().min(1)
const textSchema = z
  .object({
    title: line,
    /** CH-12a: the HUD scan panel's title for a hulk (it has no parts). */
    scanTitle: line,
    /** CH-12a: the survey card's close button. */
    close: line,
    hulls: z.record(z.string().min(1), line),
    /** Every stat's label (zod 4: a record over an enum is exhaustive). */
    stats: z.record(hulkStatSchema, line),
    flawsHeader: line,
    /** PF-2: the flaws line of a hulk with none. */
    flawsNone: line,
    flawRow: line,
    edge: line,
    value: line,
    coolant: line,
    coolantVented: line,
    squatters: line,
    squatCleared: line,
    kitHeader: line,
    goodRow: line,
    /** `noSite`: work at a hulk not surveyed (CH-12a). */
    refusals: z.object({ scanner: line, notEligible: line, noSite: line }).strict(),
    /** CH-12a: the HUD's interaction verbs at a hulk. */
    prompts: z.object({ vent: line, fence: line, fight: line, start: line, deliver: line, claim: line }).strict(),
    /** PF-2: the hangar's open hulk claims, each with an *Abandon claim* that needs no trip to the hulk. */
    claims: z.object({ header: line, row: line, abandon: line }).strict(),
    /** M9 gap: the hangar's *Scrap* buttons are off for a ship in a wing group (the worker refuses `busy`). */
    hangar: z.object({ scrapInWingGroup: line }).strict(),
    /** CH-12a: the live loop's one-line messages and refusals (tokens in braces). */
    live: z
      .object({
        found: line,
        kitStarted: line,
        /** CH-12e: the survey card's *Abandon the claim*. */
        kitAbandoned: line,
        abandonRefused: line,
        /** PF-2: an open claim whose hulk can't come back was closed ({hull}). */
        claimLost: line,
        delivered: line,
        kitComplete: line,
        claimed: line,
        /** CH-12g: the claim's ship goes to the home station (the last dock resolved to no station the worker knows). */
        claimedHome: line,
        /** CH-12g: the name of a last dock in another system whose site the layouts don't list ({system}). */
        stationIn: line,
        squatOut: line,
        squatLeft: line,
        busy: line,
        speed: line,
        tooManyOpen: line,
        nothing: line,
        claimRefused: line,
        noFleet: line,
        claimPending: line,
        /** R3-2: a claim order the fleet link has not answered in `claim.answerTimeoutS` (it stays queued). */
        claimSlow: line,
      })
      .strict(),
  })
  .strict()

/** Strategic `hulls.json` id → draw weight per region (one placement table's hulls). */
const eligibleHullsSchema = z
  .record(z.string().min(1), perRegion(z.number().nonnegative()))
  .refine((r) => Object.keys(r).length > 0, 'no eligible hulls')
export type EligibleHulls = z.infer<typeof eligibleHullsSchema>

const hulkPlacementTableSchema = z.object({ eligibleHulls: eligibleHullsSchema }).strict()
export type HulkPlacementTable = z.infer<typeof hulkPlacementTableSchema>

/**
 * M10 P3b1 (spec §6.11): placement tables by positive-integer key, contiguous from `"1"`. Integer-like keys enumerate
 * ascending in JS, so table order is numeric.
 */
const placementTablesSchema = z
  .record(z.string().regex(/^[1-9][0-9]*$/, 'a placement table key is a positive integer'), hulkPlacementTableSchema)
  .superRefine((tables, ctx) => {
    const keys = Object.keys(tables).map(Number)
    if (keys.length === 0 || keys.some((k, i) => k !== i + 1)) {
      ctx.addIssue({ code: 'custom', path: [], message: 'placement tables are keyed 1..N with none missing' })
    }
  })

/** The union of every table's hulls, in table order (the first table that names a hull gives its weights). */
function unionOf(tables: Readonly<Record<string, HulkPlacementTable>>): EligibleHulls {
  const out: EligibleHulls = {}
  for (const t of Object.values(tables)) for (const [id, w] of Object.entries(t.eligibleHulls)) if (!Object.hasOwn(out, id)) out[id] = w
  return out
}

export const hulksFileSchema = z
  .object({
    comment: z.string().optional(),
    // --- CH-2a: placement ---
    enabled: z.boolean(),
    perSystem: perRegion(countRangeSchema),
    /** M10 P3b1: the versioned placement tables, `"1".."N"` (table 1 frozen; a placement change is a new table). */
    placementTables: placementTablesSchema,
    offsetM: metresRangeSchema,
    clearanceM: z.number().positive(),
    tries: z.number().int().min(1),
    excludeSystems: z.array(hulkExclusionSchema),
    // --- CH-4: survey, flaws, kit, hazards, scrap ---
    survey: z.object({ minScannerTier: z.number().int().min(1) }).strict(),
    flaws: z.array(hulkFlawSchema).min(1).max(MAX_FLAWS),
    /** Draw weight of rolling 1, 2, 3, ... flaws (index 0 = one flaw). A hulk always has at least one. */
    flawCountWeights: z.array(z.number().nonnegative()).min(1),
    /** First Rush pattern edges (D7(c)): a seeded FR hulk carries exactly one, drawn by hash. Never tetherstone. */
    frEdges: z.object({ maxMult: z.number().gt(1), edges: z.array(hulkEdgeSchema).min(1).max(MAX_EDGES) }).strict(),
    kit: z
      .object({
        /** Share of each `buildCost` ware, rounded up per ware. */
        share: z.number().gt(0).max(1),
        /** Fuel cells per eligible hull. */
        fuelCells: z.record(z.string().min(1), z.number().int().nonnegative()),
      })
      .strict(),
    hazards: z
      .object({
        coolantShare: perRegion(share),
        squatterShare: perRegion(share),
        /** Credits that pay a squat off (M7's fence, reused by CH-6). */
        fenceFee: perRegion(z.number().int().nonnegative()),
      })
      .strict(),
    scrap: z
      .object({
        /** Scrap value = share × (1 − perFlaw × flaw count) × hull value (CH-11; tuned by the H-5 data test). */
        share: z.number().gt(0).max(1),
        perFlaw: z.number().min(0).max(1),
      })
      .strict(),
    /** CH-9b: a fleet ship's off-screen combat strength × this per flaw. */
    combat: z.object({ strengthMultPerFlaw: z.number().gt(0).max(1) }).strict(),
    /** CH-8b (D-572): a claimed ship insures at most `putInShare` × what was put in (kit + yard fixes, base price). */
    insurance: z.object({ putInShare: z.number().gt(0).max(1) }).strict(),
    /** R3-2: seconds before an unanswered claim order says so on comms (`text.live.claimSlow`). */
    claim: z.object({ answerTimeoutS: z.number().positive() }).strict(),
    text: textSchema,
  })
  .strict()
  .superRefine((cfg, ctx) => {
    for (const [table, { eligibleHulls }] of Object.entries(cfg.placementTables)) {
      for (const region of ['gate', 'mid', 'deep', 'rim'] as const) {
        if (cfg.perSystem[region][1] === 0) continue
        const total = Object.values(eligibleHulls).reduce((sum, w) => sum + w[region], 0)
        if (total <= 0) {
          ctx.addIssue({ code: 'custom', path: ['placementTables', table, 'eligibleHulls'], message: `region "${region}" places hulks but no hull has weight there` })
        }
      }
    }
    const unique = (path: string, ids: readonly string[]): void => {
      if (new Set(ids).size !== ids.length) ctx.addIssue({ code: 'custom', path: [path], message: 'duplicate ids' })
    }
    unique('flaws', cfg.flaws.map((f) => f.id))
    unique('frEdges', cfg.frEdges.edges.map((e) => e.id))
    if (cfg.flawCountWeights.length > cfg.flaws.length) {
      ctx.addIssue({ code: 'custom', path: ['flawCountWeights'], message: 'a count above the flaw list' })
    }
    if (cfg.flawCountWeights.reduce((s, w) => s + w, 0) <= 0) ctx.addIssue({ code: 'custom', path: ['flawCountWeights'], message: 'no weight' })
    for (const e of cfg.frEdges.edges) {
      if (e.mult > cfg.frEdges.maxMult) ctx.addIssue({ code: 'custom', path: ['frEdges', e.id], message: 'edge above maxMult' })
    }
    for (const hull of Object.keys(unionOf(cfg.placementTables))) {
      if (cfg.kit.fuelCells[hull] === undefined) ctx.addIssue({ code: 'custom', path: ['kit', 'fuelCells', hull], message: 'eligible hull without fuel cells' })
      if (cfg.text.hulls[hull] === undefined) ctx.addIssue({ code: 'custom', path: ['text', 'hulls', hull], message: 'eligible hull without a name' })
    }
  })
  // The derived `eligibleHulls`: the union of every table (claimability, kits, names and the dev panel read it; placement
  // reads its own table through `placementOf`). An output key only: re-parsing a parsed config is refused (strict).
  .transform((cfg) => ({ ...cfg, eligibleHulls: unionOf(cfg.placementTables) }))
export type HulksConfig = z.infer<typeof hulksFileSchema>

/** M10 P3b1: the first placement table (M9's placement), the table of a career with no `hulkTable` key. */
export const HULK_TABLE_FIRST = 1

/**
 * M10 P3b2 (spec §6.11): the placement table of `career`'s galaxy: its `hulkTable`, absent = 1. Callers that hold a
 * career place hulks from this table, never from `HULK_TABLE_FIRST`.
 */
export function hulkTableOf(career: { readonly hulkTable?: number }): number {
  return career.hulkTable ?? HULK_TABLE_FIRST
}

/** The highest placement table this build has (the one a new galaxy would use). */
export function latestHulkTable(cfg: HulksConfig = hulksConfig()): number {
  return Math.max(...Object.keys(cfg.placementTables).map(Number))
}

/** Placement table `table`; throws for a table this build doesn't have (a career from a later build). */
export function placementOf(cfg: HulksConfig, table: number): HulkPlacementTable {
  const t = Object.hasOwn(cfg.placementTables, String(table)) ? cfg.placementTables[String(table)] : undefined
  if (t === undefined) throw new Error(`hulks: placement table ${table} is not in this build`)
  return t
}

let cached: HulksConfig | null = null
/** The parsed `hulks.json` (throws on a bad file at first use). */
export function hulksConfig(): HulksConfig {
  cached ??= hulksFileSchema.parse(hulksJson)
  return cached
}
